import {
  Component,
  input,
  output,
  computed,
  signal,
  ChangeDetectionStrategy,
  ElementRef,
  viewChildren,
  afterNextRender,
  effect,
} from '@angular/core';

export type M3TextSegmentedButtonSize = 'sm' | 'default';

export interface TextSegmentOption<T = string> {
  value: T;
  label: string;
}

@Component({
  selector: 'app-m3-text-segmented-button',
  standalone: true,
  template: `
    <div
      class="m3-text-segmented-button"
      [class.size-sm]="size() === 'sm'"
      role="radiogroup"
      [attr.aria-label]="ariaLabel()"
    >
      <div
        class="segment-indicator"
        [style.transform]="indicatorTransform()"
        [style.width.px]="indicatorWidthPx()"
      ></div>
      @for (option of options(); track option.value; let i = $index) {
        <button
          #segmentButton
          type="button"
          class="segment-button"
          [class.active]="value() === option.value"
          [class.pressing]="pressingIndex() === i && value() !== option.value"
          [class.press-release]="
            releasingIndex() === i && value() !== option.value
          "
          role="radio"
          [attr.aria-checked]="value() === option.value"
          (click)="selectOption(option.value)"
          (keydown)="handleKeydown($event, i)"
          (mousedown)="handleMouseDown(i)"
          (mouseup)="handleMouseUp()"
          (mouseleave)="handleMouseLeave()"
          (touchstart)="handleTouchStart(i)"
          (touchend)="handleTouchEnd()"
        >
          {{ option.label }}
        </button>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      .m3-text-segmented-button {
        display: inline-flex;
        background: var(--md-sys-color-surface-container);
        border-radius: 20px;
        padding: 4px;
        gap: 2px;
        border: 1px solid var(--md-sys-color-outline-variant);
        position: relative;
        overflow: hidden;
      }

      .segment-indicator {
        position: absolute;
        top: 4px;
        left: 4px;
        height: calc(100% - 8px);
        background: var(--md-sys-color-secondary-container);
        border-radius: 16px;
        transition:
          transform var(--md-sys-motion-expressive-fast),
          width var(--md-sys-motion-expressive-fast);
        box-shadow: 0 1px 3px rgba(from var(--md-sys-color-shadow) r g b / 0.15);
        z-index: 1;
      }

      .segment-button {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px 16px;
        border-radius: 16px;
        border: none;
        background: transparent;
        color: var(--md-sys-color-on-surface-variant);
        cursor: pointer;
        transition:
          color var(--md-sys-motion-standard-effects-fast),
          transform 150ms var(--md-sys-motion-easing-standard);
        white-space: nowrap;
        min-height: 36px;
        position: relative;
        z-index: 2;
        font-family: inherit;
        font-size: 14px;
        font-weight: 500;

        // State layer pseudo-element
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: var(--md-sys-color-on-surface);
          opacity: 0;
          pointer-events: none;
          transition: opacity 150ms var(--md-sys-motion-easing-standard);
        }

        // Hover state (unselected only)
        &:not(.active):hover {
          color: var(--md-sys-color-on-surface);

          &::before {
            opacity: 0.08;
          }
        }

        // Pressed state - uses transition, not animation
        &:not(.active).pressing {
          transform: scale(0.96);

          &::before {
            opacity: 0.12;
          }
        }

        // Press release - transition handles fade back to hover/idle state
        &:not(.active).press-release {
          &::before {
            transition: opacity 200ms var(--md-sys-motion-easing-standard);
          }
        }

        &.active {
          color: var(--md-sys-color-on-secondary-container);
        }

        &:focus-visible {
          outline: 2px solid var(--md-sys-color-primary);
          outline-offset: 2px;
          z-index: 3;
        }

        &:focus:not(:focus-visible) {
          outline: none;
        }
      }

      // Size variant: sm
      .m3-text-segmented-button.size-sm {
        padding: 3px;
        border-radius: 16px;

        .segment-indicator {
          top: 3px;
          left: 3px;
          height: calc(100% - 6px);
          border-radius: 13px;
        }

        .segment-button {
          padding: 6px 12px;
          min-height: 30px;
          border-radius: 13px;
          font-size: 13px;

          // Smaller scale for sm variant
          &:not(.active).pressing {
            transform: scale(0.97);
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class M3TextSegmentedButtonComponent<T = string> {
  private segmentButtons = viewChildren<ElementRef>('segmentButton');

  value = input.required<T>();
  options = input.required<TextSegmentOption<T>[]>();
  size = input<M3TextSegmentedButtonSize>('default');
  ariaLabel = input<string>();

  valueChange = output<T>();

  private buttonWidths = signal<number[]>([]);
  private measurementComplete = signal(false);

  pressingIndex = signal<number | null>(null);
  releasingIndex = signal<number | null>(null);
  private releaseTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    afterNextRender(() => {
      this.measureButtonWidths();
    });

    effect(() => {
      this.options();
      this.measureButtonWidths();
    });
  }

  private measureButtonWidths(): void {
    requestAnimationFrame(() => {
      const buttons = this.segmentButtons();
      if (buttons.length === 0) return;

      const widths = buttons.map(
        (btn) => (btn.nativeElement as HTMLElement).offsetWidth
      );
      this.buttonWidths.set(widths);
      this.measurementComplete.set(true);
    });
  }

  indicatorWidthPx = computed(() => {
    const widths = this.buttonWidths();
    const opts = this.options();
    const currentValue = this.value();
    const index = opts.findIndex((opt) => opt.value === currentValue);

    if (index < 0 || widths.length === 0) {
      return this.size() === 'sm' ? 60 : 70;
    }

    return widths[index] || (this.size() === 'sm' ? 60 : 70);
  });

  indicatorTransform = computed(() => {
    const opts = this.options();
    const currentValue = this.value();
    const index = opts.findIndex((opt) => opt.value === currentValue);
    if (index < 0) return 'translateX(0)';

    const widths = this.buttonWidths();
    if (widths.length === 0) {
      const fallbackWidth = this.size() === 'sm' ? 60 : 70;
      const gap = 2;
      return `translateX(${index * (fallbackWidth + gap)}px)`;
    }

    const gap = 2;
    let offset = 0;
    for (let i = 0; i < index; i++) {
      offset += (widths[i] || 0) + gap;
    }
    return `translateX(${offset}px)`;
  });

  selectOption(optionValue: T): void {
    if (optionValue !== this.value()) {
      this.valueChange.emit(optionValue);
    }
  }

  handleKeydown(event: KeyboardEvent, currentIndex: number): void {
    const opts = this.options();
    let newIndex = currentIndex;
    let handled = false;

    switch (event.key) {
      case 'ArrowLeft':
        if (currentIndex > 0) {
          newIndex = currentIndex - 1;
          handled = true;
        }
        break;
      case 'ArrowRight':
        if (currentIndex < opts.length - 1) {
          newIndex = currentIndex + 1;
          handled = true;
        }
        break;
      case 'Home':
        newIndex = 0;
        handled = true;
        break;
      case 'End':
        newIndex = opts.length - 1;
        handled = true;
        break;
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();

      if (newIndex !== currentIndex) {
        this.selectOption(opts[newIndex].value);
        this.focusButton(newIndex);
      }
    }
  }

  private focusButton(index: number): void {
    const buttons = this.segmentButtons();
    if (buttons[index]) {
      (buttons[index].nativeElement as HTMLButtonElement).focus();
    }
  }

  handleMouseDown(index: number): void {
    this.startPress(index);
  }

  handleMouseUp(): void {
    this.endPress();
  }

  handleMouseLeave(): void {
    if (this.pressingIndex() !== null) {
      this.endPress();
    }
  }

  handleTouchStart(index: number): void {
    this.startPress(index);
  }

  handleTouchEnd(): void {
    this.endPress();
  }

  private startPress(index: number): void {
    if (this.releaseTimeout) {
      clearTimeout(this.releaseTimeout);
      this.releaseTimeout = null;
    }
    this.releasingIndex.set(null);
    this.pressingIndex.set(index);
  }

  private endPress(): void {
    const currentIndex = this.pressingIndex();
    if (currentIndex === null) return;

    this.pressingIndex.set(null);
    this.releasingIndex.set(currentIndex);

    this.releaseTimeout = setTimeout(() => {
      this.releasingIndex.set(null);
      this.releaseTimeout = null;
    }, 200);
  }
}
